import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useAuditLogs,
  useDeleteAuditLog,
  useBulkDeleteAuditLogs,
  useAuditPurgePreview,
  useRetentionPolicies,
  useSaveRetentionPolicy,
} from '../../services/httpServices/queries';
import type { RetentionUnit } from '../../types/retention';
import { usePermissions } from '../../hooks/usePermissions';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { RetentionPurgeModal } from '../../components/ui/RetentionPurgeModal';
import { formatOrdinalDateTime } from '../../utils/dateFormat';
import { toast } from 'sonner';
import {
  ClipboardList,
  Loader2,
  AlertCircle,
  User,
  Clock,
  Database,
  FileText,
  Search,
  X,
  Trash2,
  CalendarClock,
} from 'lucide-react';

/** Converts backend tokens like "UPDATE_STATUS" / "diagnostic_routing" to "Update Status" / "Diagnostic Routing". */
function humanize(value?: string): string {
  if (!value) return '—';
  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function Audit() {
  const navigate = useNavigate();
  const location = useLocation();
  const limit = 50;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);

  const { data, isLoading, error } = useAuditLogs(page, limit, debouncedSearch || undefined);
  const logs = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;

  const deleteLog = useDeleteAuditLog();
  const bulkDeleteLogs = useBulkDeleteAuditLogs();
  const { hasPermission } = usePermissions();
  const canViewRetention =
    hasPermission('retention:read') || hasPermission('retention:manage');
  const canManageRetention = hasPermission('retention:manage');

  const saveRetentionPolicy = useSaveRetentionPolicy();
  const { data: retentionData } = useRetentionPolicies(canViewRetention);

  // Row selection for bulk actions.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // Retention purge ("remove old logs") state.
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [retentionValue, setRetentionValue] = useState('90');
  const [retentionUnit, setRetentionUnit] = useState<RetentionUnit>('days');
  const parsedRetention = Number(retentionValue);
  const retentionValid = Number.isInteger(parsedRetention) && parsedRetention > 0;
  const {
    data: purgePreview,
    isFetching: purgePreviewLoading,
    isError: purgePreviewError,
  } = useAuditPurgePreview(
    retentionValid ? parsedRetention : 0,
    retentionUnit,
    purgeOpen,
  );

  const openPurge = () => {
    const policy = retentionData?.data?.policies?.find(
      (p) => p.resource === 'audit_logs',
    );
    setRetentionValue(String(policy?.value ?? 365));
    setRetentionUnit(policy?.unit ?? 'days');
    setPurgeOpen(true);
  };

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(logs.map((l: any) => l.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [logs]);

  const allSelected = logs.length > 0 && logs.every((l: any) => selectedIds.has(l.id));
  const someSelected = logs.some((l: any) => selectedIds.has(l.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = logs.length > 0 && logs.every((l: any) => prev.has(l.id));
      return everySelected ? new Set() : new Set(logs.map((l: any) => l.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteLog.mutateAsync(deleteTarget.id);
      toast.success('Audit log deleted');
    } catch {
      toast.error('Failed to delete audit log');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      await bulkDeleteLogs.mutateAsync(ids);
      setSelectedIds(new Set());
      toast.success(`Deleted ${ids.length} audit log${ids.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to delete some audit logs');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  const handleSavePolicy = async (value: number, unit: RetentionUnit) => {
    try {
      await saveRetentionPolicy.mutateAsync({
        resource: 'audit_logs',
        value,
        unit,
      });
      setPurgeOpen(false);
      toast.success('Automatic audit log cleanup saved');
    } catch {
      toast.error('Failed to save cleanup settings');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">Audit Logs</h1>
        </div>
        {canManageRetention && (
          <button
            type="button"
            onClick={openPurge}
            className="px-3 py-2 text-sm font-medium text-danger border border-danger/30 hover:bg-danger/10 rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap"
          >
            <CalendarClock size={16} />
            Remove old logs
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by user, action, module, details or IP..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          {search && (
            <button
              onClick={() => { setSearch(''); setPage(1); }}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteLogs.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteLogs.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteLogs.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load audit logs</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={logs.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Module</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Details</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">IP Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Timestamp</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-text-muted">
                      {search ? (
                        <span>No logs match your search.{' '}
                          <button onClick={() => { setSearch(''); setPage(1); }} className="text-primary hover:underline">
                            Clear search
                          </button>
                        </span>
                      ) : (
                        'No audit logs found'
                      )}
                    </td>
                  </tr>
                ) : (
                  logs.map((log: any) => (
                    <tr key={log.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(log.id)}
                          onChange={() => toggleOne(log.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <User size={14} className="text-primary" />
                          </div>
                          <div className="min-w-0">
                            <span className="block text-sm text-text truncate">
                              {log.userName || log.username || (log.userId ? log.userId.slice(0, 8) : 'System')}
                            </span>
                            {log.username && log.userName && log.userName !== log.username && (
                              <span className="block text-xs text-text-muted truncate">@{log.username}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          log.action?.includes('CREATE') ? 'bg-success/10 text-success' :
                          log.action?.includes('DELETE') ? 'bg-danger/10 text-danger' :
                          log.action?.includes('UPDATE') ? 'bg-primary/10 text-primary' :
                          'bg-secondary/10 text-secondary'
                        }`}>
                          {humanize(log.action)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-text">
                          <Database size={14} className="text-text-muted shrink-0" />
                          {humanize(log.module)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2 text-sm text-text-muted max-w-xs">
                          <FileText size={14} className="shrink-0 mt-0.5" />
                          <span className="break-words" title={log.endpoint ? `Endpoint: ${log.endpoint}` : undefined}>
                            {log.message || humanize(log.action)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">{log.ipAddress || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-text-muted whitespace-nowrap">
                          <Clock size={14} />
                          {formatOrdinalDateTime(log.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setDeleteTarget(log)}
                          className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-danger transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="logs"
          />
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete audit log?"
        description="This will remove the selected audit log entry. This cannot be undone."
        confirmLabel="Delete"
        loading={deleteLog.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} audit log${selectedIds.size === 1 ? '' : 's'}?`}
        description="This will remove the selected audit log entries. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size} audit log${selectedIds.size === 1 ? '' : 's'}`}
        loading={bulkDeleteLogs.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />

      <RetentionPurgeModal
        open={purgeOpen}
        loading={saveRetentionPolicy.isPending}
        itemSingular="audit log"
        itemPlural="audit logs"
        value={retentionValue}
        unit={retentionUnit}
        onValueChange={setRetentionValue}
        onUnitChange={setRetentionUnit}
        preview={purgePreview?.data}
        previewLoading={purgePreviewLoading}
        previewError={purgePreviewError}
        nextRunAt={retentionData?.data?.schedule?.nextRunAt}
        timezone={retentionData?.data?.schedule?.timezone}
        onClose={() => setPurgeOpen(false)}
        onConfirm={handleSavePolicy}
      />
    </div>
  );
}
